import { useRef, type ComponentType } from 'react';
import styled from '@emotion/styled';
import { layers } from 'app/spacing';
import { Icon } from '@kizen/kds/Icon';
import { Typography } from '@kizen/kds/Typography';
import { Spacer } from '@kizen/kds/Spacer';
import type {
  AIUsageAgenticWorkflowEntity,
  AIUsageAmount,
  AIUsageEmployeeEntity,
} from '@kizen/api/billing';
import BasicTable, {
  TABLE_HEADER_HEIGHT,
  TABLE_ROW_HEIGHT,
} from '__components/Tables/Basic';
import { TRow } from '__components/Kizen/Table';
import Loader from '__components/Kizen/Loader';
import ScrollFadeContainer, {
  Gradient,
} from '__components/Kizen/ScrollFadeContainer';
import useEndsOfScroll from 'hooks/useEndsOfScroll';
import TablePagination from 'components/Tables/Big/TablePagination';
import { useTranslation } from 'react-i18next';
import { useBreakdownColumns } from './columns';
import { getKizenCreditsOnlyColumns } from './utils';
import type { Bucket, UsageBreakdownData } from './useUsageBreakdownData';
import {
  TableScrollArea,
  LoaderOverlay,
  ErrorOverlay,
  GRADIENT_Z_INDEX,
} from './styles';

const BreakdownTableWrapper = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
`;

const StyledTablePagination = styled(TablePagination)`
  flex-shrink: 0;
  margin-top: 5px;

  .PageNavWrapper {
    justify-content: flex-end;
  }
`;

// Grows to fill whatever vertical space the modal has available, instead of
// a fixed row count — rows beyond what fits scroll internally. On short
// viewports the table still reserves room for 5 rows (4 readable above the
// bottom fade)
const MIN_VISIBLE_ROWS = 5;

const TOP_GRADIENT_Z_INDEX = layers.content(0, 0);

const BreakdownTableScrollArea = styled(TableScrollArea)`
  flex: 1;
  min-height: ${TABLE_HEADER_HEIGHT + MIN_VISIBLE_ROWS * TABLE_ROW_HEIGHT}px;
  width: 100%;
  overflow-y: auto;
  overscroll-behavior: none;

  &::-webkit-scrollbar-track:vertical {
    margin-top: ${TABLE_HEADER_HEIGHT - 1}px;
    border-top: 1px solid var(--color-border-medium-light);
  }

  & table {
    min-width: auto !important;
    align-self: auto !important;
    table-layout: auto !important;
  }
` as ComponentType<any>;

type BreakdownRow = {
  agentic_workflow?: AIUsageAgenticWorkflowEntity;
  employee?: AIUsageEmployeeEntity;
  usage: AIUsageAmount[];
};

type Props = {
  bucket: Bucket;
  data: UsageBreakdownData['data'];
  isLoading: boolean;
  isError: boolean;
  pagination: UsageBreakdownData['pagination'];
};

export const UsageBreakdownTable = ({
  bucket,
  data,
  isLoading,
  isError,
  pagination,
}: Props) => {
  const scrollRef = useRef<HTMLDivElement>(null);

  const { t } = useTranslation();
  const { page, pageSize, headData } = pagination;

  const columns = useBreakdownColumns(
    bucket,
    bucket === 'team-members'
      ? getKizenCreditsOnlyColumns(data?.columns ?? [])
      : data?.columns ?? []
  );

  const rows = (data?.results ?? []) as BreakdownRow[];

  const [topScrolled, rightScrolled, bottomScrolled, leftScrolled] =
    useEndsOfScroll(scrollRef, [columns, data], 1);

  return (
    <BreakdownTableWrapper data-qa-id={`ai-usage-${bucket}-breakdown`}>
      <BreakdownTableScrollArea
        ref={scrollRef}
        as={ScrollFadeContainer}
        className="kds-expanding-scrollbar"
        top={{
          fadeStart: TABLE_HEADER_HEIGHT,
          zIndex: TOP_GRADIENT_Z_INDEX,
        }}
        right
        bottom
        left
        scrolled={[topScrolled, rightScrolled, bottomScrolled, leftScrolled]}
        gradient={<Gradient zIndex={GRADIENT_Z_INDEX} />}
      >
        <BasicTable
          stickyHeader
          head={<TRow head columns={columns} data={headData} />}
          data-qa-id={`ai-usage-${bucket}-table`}
          staleData={isLoading || isError}
        >
          {rows.map((row) => (
            <TRow
              key={row.agentic_workflow?.id ?? row.employee?.id}
              columns={columns}
              data={row}
            />
          ))}
        </BasicTable>
        {isError ? (
          <ErrorOverlay data-qa-id={`ai-usage-${bucket}-error`}>
            <Icon color="feedback-error" icon="message-info-circle" />
            <Spacer size={5} mode="vertical" />
            <Typography color="feedback-error">
              {t('Failed to load this data.')}
            </Typography>
          </ErrorOverlay>
        ) : isLoading ? (
          <LoaderOverlay>
            <Loader loading paddingTop={0} />
          </LoaderOverlay>
        ) : null}
      </BreakdownTableScrollArea>
      <StyledTablePagination
        page={page}
        perPage={pageSize}
        totalCount={data?.count}
        onChangePage={pagination.handleChangePage}
        onChangePerPage={pagination.handleChangePageSize}
      />
    </BreakdownTableWrapper>
  );
};
